import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'tsm_admin_token';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(undefined); // undefined = checking, null = signed out, object = signed in
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const check = useCallback(async () => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) { setAdmin(null); return; }
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${stored}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('invalid');
      setAdmin(await res.json());
      setToken(stored);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAdmin(null);
      setToken(null);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setAdmin({ email: data.email, role: data.role });
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token || ''}` },
        credentials: 'include',
      });
    } catch {}
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
    setToken(null);
  };

  const authFetch = async (path, opts = {}) => {
    const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token || ''}` };
    if (opts.body && !(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    return fetch(`${API}${path}`, { ...opts, headers, credentials: 'include' });
  };

  return (
    <AuthContext.Provider value={{ admin, token, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
